import {announce} from '@github-ui/aria-live'
import {goBack} from '@github-ui/history'
import {hpcAttrs} from '@github-ui/hpc'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {CreateMilestoneInput} from '@github-ui/server-persisted-queries/types/createRepositoryMilestoneMutation'
import {useNavigate} from '@github-ui/use-navigate'
import {Link} from '@primer/react'
import {type Dispatch, type SetStateAction, useCallback, useState} from 'react'

import type {RepositoryNode} from './components/milestone-form-query-types'
import {MilestoneForm} from './components/MilestoneForm'
import {LABELS} from './constants/labels'
import styles from './MilestoneCreateEdit.module.css'
import {MilestoneError} from './MilestoneError'
import {useCreateRepositoryMilestone} from './use-create-repository-milestone'

export type MilestoneCreateFragment = Pick<RepositoryNode, 'id' | 'nameWithOwner' | 'viewerCanPush'>

type MilestoneCreateProps = {
  repository: MilestoneCreateFragment
}

export function MilestoneCreate({repository}: MilestoneCreateProps) {
  const navigate = useNavigate()

  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)

  // Set and announce a submission error together. The error renders as a Primer Banner
  // (a focusable section, not a live region) with no focus moved to it, so a screen-reader
  // user would otherwise not learn the create failed. Routing both the Relay and TanStack
  // error paths through this covers WCAG 4.1.3 without reacting to state in an effect.
  const showSubmissionError = useCallback((message: string | null) => {
    setSubmissionErrors(message)
    if (message) {
      announce(message, {assertive: true})
    }
  }, [])

  const createMilestoneTanstack = useCreateRepositoryMilestone({
    repositoryId: repository?.id,
    repositoryNameWithOwner: repository?.nameWithOwner,
    setSubmissionErrors: showSubmissionError,
    onSuccess: milestone => {
      navigate(`/${repository?.nameWithOwner}/milestone/${milestone.number}`)
    },
  })

  const handleFormSubmit = useCallback(
    (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => {
      setSubmissionErrors(null)

      if (!repository?.viewerCanPush) {
        showSubmissionError(LABELS.milestoneCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      createMilestoneTanstack(input, setIsSubmitting)
    },
    [repository, createMilestoneTanstack, showSubmissionError],
  )

  const handleFormCancel = () => {
    goBack()
  }

  const formDescription = (
    <p className={styles.milestonePageDescription}>
      {LABELS.createMilestoneDescription} {LABELS.learnMorePrefix}{' '}
      <Link href="https://docs.github.com/en/issues/tracking-your-work-with-issues/about-issues" inline>
        {LABELS.milestonesAndIssues}
      </Link>
      .
    </p>
  )

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  return (
    <div
      className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}
      {...hpcAttrs()}
      data-testid="milestone-create"
    >
      <ErrorBoundary
        fallback={<MilestoneError title={LABELS.milestonePageError} message={LABELS.milestonePageErrorMessage} />}
      >
        <MilestoneForm
          repository={repository}
          formTitle={LABELS.createMilestone}
          formDescription={formDescription}
          formSubmitLabel={LABELS.createMilestone}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          submissionErrors={submissionErrors}
        />
      </ErrorBoundary>
    </div>
  )
}
